import { Component, createRef } from 'preact';
import DeckCardTile from './DeckCardTile';
import CardGrid from './CardGrid';
import CardFilterBar from './CardFilterBar';
import RuneSpinner from './RuneSpinner';
import VikingOrnament from './VikingOrnament';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  PANEL_BG, BEVELED_BTN, FourCorners,
} from '../lib/medievalTheme';
import { disenchantRefundForRarity } from '../utils/arena/profileDefaults';
import { slotRarityForCard } from '../utils/sorcery/slotRarityOverrides';
import { disenchantCard } from '../utils/arena/matchmakingApi';
import { loadArenaProfile } from '../utils/arena/profileApi';
import { isFoilFinish } from '../utils/sorcery/foil.js';
import { foilZonesForCard } from '../utils/sorcery/foilZones';
import { buildUsedMap } from '../utils/arena/collectionUtils';
import {
  ART_GROUP_BASE, artGroupLabel, artGroupDisplayName, printingsInArtGroup,
} from '../utils/sorcery/artGroups';
import { MAX_COPIES_BY_RARITY, MAX_COPIES_DEFAULT } from '../utils/sorcery/deckRules';
import { ShardIcon } from './ui/icons';
import { playUI, UI } from '../utils/arena/uiSounds';
import { t } from '../localization/t';

const SHARD_COLOR = '#7dd3fc';

const RARITY_COLORS = {
  Ordinary: TEXT_MUTED,
  Exceptional: '#6ea8d4',
  Elite: '#b480d4',
  Unique: ACCENT_GOLD,
};

const RARITY_ORDER = { Unique: 0, Elite: 1, Exceptional: 2, Ordinary: 3 };

// Build the list of disenchant-eligible entries for the player.
// Only non-foil, non-Avatar cards with a refund tier are eligible.
// `usedMap` is the per-card count of copies tied up in saved decks
// so the UI never offers a card the player is actively using.
function buildDisenchantEntries(collection, usedMap, sorceryCards) {
  const cardIndex = new Map((sorceryCards || []).map((c) => [c.unique_id, c]));
  // One entry per (card, art group): the collection stores them as separate
  // rows and the server consumes from the row you name, so a single merged
  // entry would offer more copies than any one row can actually satisfy.
  const owned = new Map();
  for (const row of collection || []) {
    if (isFoilFinish(row.foiling)) continue;
    const artGroup = row.artGroup || ART_GROUP_BASE;
    const key = `${row.cardId}\u0001${artGroup}`;
    const prev = owned.get(key);
    if (prev) prev.ownedCount += row.quantity || 0;
    else owned.set(key, { cardId: row.cardId, artGroup, ownedCount: row.quantity || 0 });
  }
  const entries = [];
  for (const { cardId, artGroup, ownedCount } of owned.values()) {
    const card = cardIndex.get(cardId);
    if (!card) continue;
    if (card.type === 'Avatar') continue;
    const refundEach = disenchantRefundForRarity(slotRarityForCard(card));
    if (refundEach == null) continue;
    // Deck usage is tracked per card, not per art. Charging the whole card's
    // deck usage against every art would hide arts the player isn't using;
    // charging it against none would offer a copy that's in a deck. Bill it
    // to the base art, which is what decks overwhelmingly hold.
    const used = artGroup === ART_GROUP_BASE ? (usedMap?.get(cardId) || 0) : 0;
    const available = Math.max(0, ownedCount - used);
    if (available <= 0) continue;
    entries.push({ card, artGroup, ownedCount, used, available, refundEach });
  }
  return entries;
}

// Bulk-disenchant targets are ownership-agnostic: a single owned copy
// can be used across every deck the player builds, so deck-usage is
// irrelevant here. Only the playset cap (rarity max) matters. Anything
// above that is fair game to dust.
function buildBulkDisenchantTargets(collection, sorceryCards) {
  const cardIndex = new Map((sorceryCards || []).map((c) => [c.unique_id, c]));
  const owned = new Map();
  for (const row of collection || []) {
    if (isFoilFinish(row.foiling)) continue;
    const artGroup = row.artGroup || ART_GROUP_BASE;
    const key = `${row.cardId}\u0001${artGroup}`;
    const prev = owned.get(key);
    if (prev) prev.ownedCount += row.quantity || 0;
    else owned.set(key, { cardId: row.cardId, artGroup, ownedCount: row.quantity || 0 });
  }
  const targets = [];
  for (const { cardId, artGroup, ownedCount } of owned.values()) {
    const card = cardIndex.get(cardId);
    if (!card) continue;
    if (card.type === 'Avatar') continue;
    const refundEach = disenchantRefundForRarity(slotRarityForCard(card));
    if (refundEach == null) continue;
    // A playset PER ART, not per card. The copy cap is card-wide, so four
    // booster copies technically make an alt-art copy redundant for deck
    // purposes. But dusting someone's alt art because they also own
    // boosters of it destroys the rarer collectible of the two.
    const playset = MAX_COPIES_BY_RARITY[card.rarity] || MAX_COPIES_DEFAULT;
    const disenchantable = Math.max(0, ownedCount - playset);
    if (disenchantable <= 0) continue;
    targets.push({ card, artGroup, ownedCount, playset, disenchantable, refundEach });
  }
  return targets;
}

function pickStandardPrinting(card, artGroup = ART_GROUP_BASE) {
  const inGroup = printingsInArtGroup(card, artGroup);
  return inGroup.find((p) => !isFoilFinish(p.foiling)) || inGroup[0] || null;
}

// Grid / selection identity: a card in one illustration.
const entryId = (cardId, artGroup) => `${cardId}\u0001${artGroup || ART_GROUP_BASE}`;

export default class StoreDisenchantTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      matchesCard: () => true,
      sortBy: 'value',
      selectedCardId: null,
      qty: 1,
      busyCardId: null,
      error: null,
      flash: null,
      bulkBusy: false,
      bulkArmed: false,
      bulkProgress: null,
    };
    this.gridRef = createRef();
  }

  handleFilterChange = ({ matchesCard }) => {
    this.setState({ matchesCard });
    this.gridRef.current?.resetScroll();
  };

  handleSelect = (id) => {
    const entries = this._cachedEntries || [];
    const e = entries.find((x) => entryId(x.card.unique_id, x.artGroup) === id);
    this.setState({
      selectedCardId: id,
      qty: e ? Math.min(1, e.available) : 1,
      error: null,
    });
  };

  handleDisenchant = async (entry) => {
    const qty = Math.max(1, Math.min(parseInt(this.state.qty, 10) || 1, entry.available));
    this.setState({ busyCardId: entryId(entry.card.unique_id, entry.artGroup), error: null });
    try {
      const result = await disenchantCard({
        cardId: entry.card.unique_id, artGroup: entry.artGroup, quantity: qty,
      });
      playUI(UI.GOLD);
      const fresh = await loadArenaProfile(this.props.profile.serverToken).catch(() => null);
      if (fresh && this.props.onProfileUpdate) {
        this.props.onProfileUpdate({
          ...this.props.profile,
          collection: fresh.collection ?? this.props.profile.collection,
          arcanaShards: fresh.arcanaShards ?? result.newTotals?.arcanaShards ?? this.props.profile.arcanaShards,
        });
      }
      this.setState({
        flash: t('+{n} arcana', { n: result.shardsGained.toLocaleString() }),
        // Recompute selection: if the player just emptied this stack
        // (available was qty), drop the selection. Otherwise keep
        // the same card selected with qty reset to 1.
        selectedCardId: entry.available > qty ? entryId(entry.card.unique_id, entry.artGroup) : null,
        qty: 1,
        busyCardId: null,
      });
      clearTimeout(this._flashTimer);
      this._flashTimer = setTimeout(() => this.setState({ flash: null }), 2400);
    } catch (err) {
      this.setState({ error: err.message || t('Failed to disenchant'), busyCardId: null });
    }
  };

  armBulkDisenchant = () => {
    this.setState({ bulkArmed: true, error: null });
    clearTimeout(this._armTimer);
    this._armTimer = setTimeout(() => this.setState({ bulkArmed: false }), 4000);
  };

  handleDisenchantExtras = async () => {
    clearTimeout(this._armTimer);
    const targets = (this._extraDisenchantTargets || []).filter((e) => e.disenchantable > 0);
    if (targets.length === 0) {
      this.setState({ bulkArmed: false });
      return;
    }
    this.setState({
      bulkArmed: false,
      bulkBusy: true,
      bulkProgress: { done: 0, total: targets.length },
      error: null,
    });
    let totalGained = 0;
    let failures = 0;
    for (let i = 0; i < targets.length; i += 1) {
      const t = targets[i];
      try {
        const res = await disenchantCard({
          cardId: t.card.unique_id, artGroup: t.artGroup, quantity: t.disenchantable,
        });
        totalGained += res.shardsGained || 0;
      } catch (err) {
        failures += 1;
      }
      this.setState({ bulkProgress: { done: i + 1, total: targets.length } });
    }
    playUI(UI.GOLD);
    const fresh = await loadArenaProfile(this.props.profile.serverToken).catch(() => null);
    if (fresh && this.props.onProfileUpdate) {
      this.props.onProfileUpdate({
        ...this.props.profile,
        collection: fresh.collection ?? this.props.profile.collection,
        arcanaShards: fresh.arcanaShards ?? this.props.profile.arcanaShards,
      });
    }
    this.setState({
      flash: failures === 0
        ? t('+{n} arcana', { n: totalGained.toLocaleString() })
        : failures === 1
        ? t('+{n} arcana (1 card failed)', { n: totalGained.toLocaleString() })
        : t('+{n} arcana ({failed} cards failed)', { n: totalGained.toLocaleString(), failed: failures }),
      bulkBusy: false,
      bulkProgress: null,
      selectedCardId: null,
      qty: 1,
    });
    clearTimeout(this._flashTimer);
    this._flashTimer = setTimeout(() => this.setState({ flash: null }), 3600);
  };

  componentWillUnmount() {
    if (this._flashTimer) clearTimeout(this._flashTimer);
    if (this._armTimer) clearTimeout(this._armTimer);
  }

  getItemKey = (entry) => entryId(entry.card.unique_id, entry.artGroup);

  // Disenchant only ever lists non-foil entries (buildDisenchantEntries skips
  // foilings), so this is effectively always null. Mirrored for consistency
  // with the other grids so the shared scene path is wired the same way.
  getItemFoil = (entry) => {
    const printing = pickStandardPrinting(entry.card, entry.artGroup);
    if (!printing || !isFoilFinish(printing.foiling)) return null;
    return { imageUrl: printing.image_url || '', zones: foilZonesForCard(entry.card) };
  };

  getItemCellStyle = (entry) => (
    this.state.selectedCardId === entryId(entry.card.unique_id, entry.artGroup) ? { zIndex: 10 } : {}
  );

  renderTile = (entry, { imageHidden, onTilt }) => {
    const card = entry.card;
    const printing = pickStandardPrinting(card, entry.artGroup);
    const id = entryId(card.unique_id, entry.artGroup);
    const isSelected = this.state.selectedCardId === id;
    return (
      <div
        className="cursor-pointer"
        onClick={() => this.handleSelect(id)}
      >
        {printing ? (
          <DeckCardTile
            entry={{ card, printing, zone: 'spellbook', entryIndex: 0 }}
            isSelected={isSelected}
            imageHidden={imageHidden}
            onTilt={onTilt}
            onClick={() => this.handleSelect(id)}
          />
        ) : (
          <div className="w-full rounded-card aspect-[63/88] flex items-center justify-center text-[0.625cqw]" style={{ background: 'rgba(0,0,0,0.4)', color: TEXT_MUTED }}>
            {card.name}
          </div>
        )}
      </div>
    );
  };

  renderFooter = (entry) => {
    const card = entry.card;
    const groupLabel = artGroupLabel(entry.artGroup);
    return (
      <>
        <div className="text-center mt-1 text-[0.5625cqw] font-bold" style={{ color: ACCENT_GOLD }}>
          ×{entry.available}
        </div>
        <div className="text-xs font-semibold truncate mt-1 text-center" style={{ color: TEXT_PRIMARY }}>
          {artGroupDisplayName(card, entry.artGroup)}
        </div>
        <div className="flex items-center justify-center gap-1 mt-0.5 text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
          <span style={{ color: RARITY_COLORS[card.rarity] || TEXT_MUTED }}>{card.rarity}</span>
          {groupLabel && <span style={{ color: ACCENT_GOLD }}>{groupLabel}</span>}
        </div>
        <div className="flex items-center justify-center gap-1 mt-0.5">
          <ShardIcon size={11} />
          <span className="text-xs font-bold tabular-nums" style={{ color: SHARD_COLOR }}>
            +{entry.refundEach}
          </span>
          <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>{t('each')}</span>
        </div>
      </>
    );
  };

  render() {
    const { sorceryCards, profile, savedDecks } = this.props;
    const {
      matchesCard, sortBy, selectedCardId, qty, busyCardId, error, flash,
      bulkBusy, bulkArmed, bulkProgress,
    } = this.state;

    const usedMap = buildUsedMap(savedDecks || [], null);
    const entries = buildDisenchantEntries(profile?.collection, usedMap, sorceryCards);
    this._cachedEntries = entries;

    let filtered = entries.filter((e) => matchesCard(e.card));
    if (sortBy === 'value') {
      filtered = [...filtered].sort((a, b) => {
        if (a.refundEach !== b.refundEach) return b.refundEach - a.refundEach;
        return a.card.name.localeCompare(b.card.name);
      });
    } else if (sortBy === 'rarity') {
      filtered = [...filtered].sort((a, b) => {
        const ra = RARITY_ORDER[a.card.rarity] ?? 99;
        const rb = RARITY_ORDER[b.card.rarity] ?? 99;
        if (ra !== rb) return ra - rb;
        return a.card.name.localeCompare(b.card.name);
      });
    } else if (sortBy === 'duplicates') {
      filtered = [...filtered].sort((a, b) => {
        if (a.available !== b.available) return b.available - a.available;
        return a.card.name.localeCompare(b.card.name);
      });
    } else if (sortBy === 'name') {
      filtered = [...filtered].sort((a, b) => a.card.name.localeCompare(b.card.name));
    }

    const selectedEntry = selectedCardId
      ? entries.find((e) => entryId(e.card.unique_id, e.artGroup) === selectedCardId)
      : null;
    const selectedQty = selectedEntry
      ? Math.max(1, Math.min(parseInt(qty, 10) || 1, selectedEntry.available))
      : 1;
    const selectedRefund = selectedEntry ? selectedEntry.refundEach * selectedQty : 0;

    // Aggregate refund preview for the whole filtered set. Handy
    // for "if I disenchanted everything visible, how much would I
    // get?" without committing.
    const totalAvailable = filtered.reduce((sum, e) => sum + e.available, 0);

    // Bulk targets are computed from a separate, ownership-agnostic
    // pass: deck usage is ignored entirely (one copy can serve every
    // deck), so the only rule is "keep a full playset, dust the rest".
    // Same filter as the grid so the button reflects what's visible.
    const bulkTargets = buildBulkDisenchantTargets(profile?.collection, sorceryCards)
      .filter((e) => matchesCard(e.card));
    this._extraDisenchantTargets = bulkTargets;
    const totalExtras = bulkTargets.reduce((sum, e) => sum + e.disenchantable, 0);
    const extrasRefund = bulkTargets.reduce(
      (sum, e) => sum + e.disenchantable * e.refundEach, 0,
    );

    return (
      <div className="flex gap-0 flex-1 min-h-0">
        {/* Left column: Filters + Card Grid */}
        <div className="flex-[65] flex flex-col min-h-0 pr-4">
          <CardFilterBar
            className="mb-3"
            sorceryCards={sorceryCards}
            features={{ scope: false, finish: false, type: true }}
            onChange={this.handleFilterChange}
            trailing={(
              <div className="flex items-center gap-1 text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                {[
                  { id: 'value', label: t('Value') },
                  { id: 'rarity', label: t('Rarity') },
                  { id: 'duplicates', label: t('Dupes') },
                  { id: 'name', label: t('Name') },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className="px-3 py-1.5 transition-colors"
                    style={{
                      color: sortBy === opt.id ? '#e8d5a0' : 'rgba(166,160,155,0.5)',
                      background: sortBy === opt.id ? 'rgba(180,140,60,0.12)' : 'transparent',
                      borderRadius: 'var(--radius-sm)',
                    }}
                    onClick={() => this.setState({ sortBy: opt.id })}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          />

          <div className="flex items-center justify-between gap-3 text-[0.625cqw] mb-2" style={{ color: TEXT_MUTED }}>
            <span>
              {entries.length === 0
                ? t('No duplicate cards available to disenchant.')
                : filtered.length === 1
                ? t('1 unique card · {free} free · {extras} above playset', { free: totalAvailable, extras: totalExtras })
                : t('{n} unique cards · {free} free · {extras} above playset', { n: filtered.length, free: totalAvailable, extras: totalExtras })}
            </span>
            {totalExtras > 0 ? (
              <div className="flex items-center gap-2">
                <span>
                  {t('Keep a playset, dust the rest:')}{' '}
                  <span style={{ color: SHARD_COLOR }}>+{extrasRefund.toLocaleString()}</span>
                  <span className="ml-1" style={{ color: 'rgba(125,211,252,0.5)' }}>{t('arcana')}</span>
                </span>
                <button
                  type="button"
                  disabled={bulkBusy}
                  onClick={bulkArmed ? this.handleDisenchantExtras : this.armBulkDisenchant}
                  className="px-3 py-1 text-[0.625cqw] transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:hover:scale-100 disabled:cursor-not-allowed"
                  style={{
                    ...BEVELED_BTN,
                    background: bulkArmed
                      ? 'linear-gradient(180deg, rgba(196,80,80,0.22), rgba(196,80,80,0.10))'
                      : 'linear-gradient(180deg, rgba(125,211,252,0.18), rgba(125,211,252,0.08))',
                    border: bulkArmed
                      ? '1px solid rgba(196,80,80,0.55)'
                      : '1px solid rgba(125,211,252,0.5)',
                    borderRadius: 'var(--radius-sm)',
                    color: bulkArmed ? '#f0b4b4' : SHARD_COLOR,
                  }}
                >
                  {bulkBusy ? (
                    <span className="inline-flex items-center gap-1.5">
                      <RuneSpinner size={11} />
                      {bulkProgress ? t('Disenchanting {done}/{total}…', { done: bulkProgress.done, total: bulkProgress.total }) : t('Disenchanting…')}
                    </span>
                  ) : bulkArmed ? (
                    totalExtras === 1
                      ? t('Confirm · 1 extra → +{refund}', { refund: extrasRefund.toLocaleString() })
                      : t('Confirm · {n} extras → +{refund}', { n: totalExtras, refund: extrasRefund.toLocaleString() })
                  ) : (
                    <>✦ {totalExtras === 1 ? t('Disenchant 1 extra') : t('Disenchant {n} extras', { n: totalExtras })}</>
                  )}
                </button>
              </div>
            ) : null}
          </div>

          {/* Card grid. CardGrid owns its own scroll container + virtualization
              and renders any foil printing through the shared WebGL scene. Kept
              mounted (empty state overlays it) so the foil context isn't torn
              down on filter changes. */}
          <div className="flex-1 flex min-h-0 relative">
            <CardGrid
              ref={this.gridRef}
              items={filtered}
              getItemKey={this.getItemKey}
              getItemFoil={this.getItemFoil}
              getItemCellStyle={this.getItemCellStyle}
              renderTile={this.renderTile}
              renderFooter={this.renderFooter}
              measureFooter={(
                <>
                  <div className="text-center mt-1" style={{ fontSize: '0.5625cqw' }}>&nbsp;</div>
                  <div className="text-xs mt-1" style={{ lineHeight: 1.3 }}>&nbsp;</div>
                  <div className="mt-0.5 text-[0.5625cqw]">&nbsp;</div>
                  <div className="mt-0.5" style={{ height: '1cqw' }} />
                </>
              )}
            />
            {filtered.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center text-sm" style={{ color: TEXT_MUTED }}>
                {t('No duplicates match your filters.')}
              </div>
            ) : null}
          </div>
        </div>

        {/* Right column: Card Preview + Disenchant action. Same
            layout vocabulary as the Spellcraft singles tab so the
            two store tabs feel like a matched pair. */}
        <div className="flex-[35] min-h-0 overflow-hidden">
          <div className="relative p-4 h-full flex flex-col" style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.2)`, borderRadius: 'var(--radius-lg)', isolation: 'isolate' }}>
            <FourCorners />
            <VikingOrnament ornament="ringerike004" variant="centerpiece" opacity={0.08} />
            {selectedEntry ? (() => {
              const card = selectedEntry.card;
              const printing = pickStandardPrinting(card, selectedEntry.artGroup);
              const selectedId = entryId(card.unique_id, selectedEntry.artGroup);
              const selectedGroupLabel = artGroupLabel(selectedEntry.artGroup);
              return (
                <div className="flex flex-col items-center gap-3 h-full min-h-0">
                  {printing ? (
                    <img
                      src={printing.image_url}
                      alt={card.name}
                      className="rounded-card object-cover bg-black/40 shrink min-h-0"
                      style={{ aspectRatio: '63 / 88', maxWidth: '60%' }}
                    />
                  ) : (
                    <div className="rounded-card bg-black/40 flex items-center justify-center text-[0.625cqw] shrink min-h-0" style={{ aspectRatio: '63 / 88', maxWidth: '60%', color: TEXT_MUTED }}>
                      {card.name}
                    </div>
                  )}

                  <div className="w-full text-center shrink-0">
                    <div className="text-sm font-bold arena-heading" style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                      {artGroupDisplayName(card, selectedEntry.artGroup)}
                    </div>
                    <div className="text-[0.6875cqw] font-semibold mt-0.5" style={{ color: RARITY_COLORS[card.rarity] || TEXT_MUTED }}>
                      {card.rarity}{selectedGroupLabel ? ` · ${selectedGroupLabel}` : ''}
                    </div>
                    <div className="text-[0.625cqw] mt-0.5" style={{ color: TEXT_MUTED }}>
                      {t('Owned:')} <span style={{ color: ACCENT_GOLD }}>{selectedEntry.ownedCount}</span>
                      <span className="mx-1.5" style={{ color: 'rgba(166,160,155,0.4)' }}>·</span>
                      {t('Used:')} <span style={{ color: TEXT_BODY }}>{selectedEntry.used}</span>
                      <span className="mx-1.5" style={{ color: 'rgba(166,160,155,0.4)' }}>·</span>
                      {t('Free:')} <span style={{ color: ACCENT_GOLD }}>{selectedEntry.available}</span>
                    </div>
                  </div>

                  <div className="w-full shrink-0 flex flex-col gap-2 mt-auto">
                    {/* Quantity stepper. Matches the visual weight of
                        the Spellcraft Buy panel's price block. */}
                    <div className="flex items-center justify-between px-3 py-2 rounded" style={{ background: 'rgba(0,0,0,0.35)', border: `1px solid ${GOLD} 0.12)` }}>
                      <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>{t('Quantity')}</span>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          disabled={selectedQty <= 1}
                          onClick={() => this.setState({ qty: Math.max(1, selectedQty - 1) })}
                          className="w-6 h-6 flex items-center justify-center text-sm font-bold transition-all disabled:opacity-30"
                          style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-sm)' }}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min="1"
                          max={selectedEntry.available}
                          value={qty}
                          onInput={(e) => this.setState({ qty: e.target.value })}
                          onBlur={(e) => {
                            const v = Math.max(1, Math.min(parseInt(e.target.value, 10) || 1, selectedEntry.available));
                            this.setState({ qty: v });
                          }}
                          className="w-12 px-1 py-0.5 text-sm outline-none text-center tabular-nums"
                          style={{
                            background: 'rgba(0,0,0,0.4)',
                            border: `1px solid ${GOLD} 0.18)`,
                            borderRadius: 'var(--radius-sm)',
                            color: TEXT_PRIMARY,
                          }}
                        />
                        <button
                          type="button"
                          disabled={selectedQty >= selectedEntry.available}
                          onClick={() => this.setState({ qty: Math.min(selectedEntry.available, selectedQty + 1) })}
                          className="w-6 h-6 flex items-center justify-center text-sm font-bold transition-all disabled:opacity-30"
                          style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-sm)' }}
                        >
                          +
                        </button>
                        <button
                          type="button"
                          disabled={selectedQty >= selectedEntry.available}
                          onClick={() => this.setState({ qty: selectedEntry.available })}
                          className="px-2 h-6 text-[0.625cqw] transition-all disabled:opacity-30"
                          style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-sm)' }}
                        >
                          {t('Max')}
                        </button>
                      </div>
                    </div>

                    {/* Refund preview. Mirrors the Spellcraft price box. */}
                    <div className="flex items-center justify-center gap-2 py-2 rounded" style={{ background: 'rgba(0,0,0,0.35)', border: `1px solid ${GOLD} 0.12)` }}>
                      <ShardIcon size={16} />
                      <span className="text-xl font-bold tabular-nums" style={{ color: SHARD_COLOR }}>
                        +{selectedRefund.toLocaleString()}
                      </span>
                      <span className="text-[0.625cqw]" style={{ color: 'rgba(125,211,252,0.5)' }}>{t('arcana')}</span>
                    </div>

                    <div className="text-[0.625cqw] text-center" style={{ color: TEXT_MUTED }}>
                      {t('{n} per copy · ~8% of craft cost', { n: selectedEntry.refundEach.toLocaleString() })}
                    </div>

                    {error ? (
                      <div className="text-[0.625cqw] text-center" style={{ color: '#c45050' }}>{error}</div>
                    ) : null}
                    {flash ? (
                      <div className="text-[0.6875cqw] text-center font-semibold" style={{ color: ACCENT_GOLD }}>
                        {flash}
                      </div>
                    ) : null}

                    <button
                      type="button"
                      disabled={busyCardId === selectedId}
                      onClick={() => this.handleDisenchant(selectedEntry)}
                      className="w-full py-2.5 text-xs font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:hover:scale-100 disabled:cursor-not-allowed"
                      style={{
                        ...BEVELED_BTN,
                        background: 'linear-gradient(180deg, rgba(125,211,252,0.18), rgba(125,211,252,0.08))',
                        border: '1px solid rgba(125,211,252,0.5)',
                        borderRadius: 'var(--radius-sm)',
                        color: SHARD_COLOR,
                      }}
                    >
                      {busyCardId === selectedId ? (
                        <span className="flex items-center justify-center gap-2"><RuneSpinner size={14} /> {t('Disenchanting...')}</span>
                      ) : (
                        <span className="flex items-center justify-center gap-1.5">
                          ✦ {t('Disenchant ×{n}', { n: selectedQty })}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              );
            })() : (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-xs px-6" style={{ color: TEXT_MUTED }}>
                <div className="mb-2 text-sm arena-heading" style={{ color: ACCENT_GOLD }}>
                  {t('Disenchantry')}
                </div>
                <p className="leading-relaxed mb-3">
                  {t('Convert duplicate non-foil cards back into Arcana Shards. Roughly 8% of the base craft cost is refunded per copy.')}
                </p>
                <p className="text-[0.625cqw]" style={{ color: 'rgba(166,160,155,0.5)' }}>
                  {t('Select a card from the grid to begin.')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}
